import React, { FC, useEffect, useMemo } from 'react'
import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { FormDataType } from '@components/music/PaymentForm/hooks/usePaymentForm'
import Checkbox from '@components/shared/form/Checkbox'
import Dropdown from '@components/shared/form/Dropdown'
import Input from '@components/shared/form/Input'
import useAsyncEffect from '@hooks/useAsyncEffect'
import useCustomerPayment from '@hooks/useCustomerPayment'
import { useDataObject } from '@hooks/useDataObject'
import { CustomerCardAddressFields } from '@models/commerce/CustomerCardAddress'
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js'
import { canadaStates, countries, usStatesPlusPR } from '@utils/geography'
import Storage from '@utils/storage'

import UsernameForm from './UsernameForm'

interface Props {
  setToken: (val: string | null) => void
  formData: FormDataType
  setFormData: (data: Partial<FormDataType>) => void
}

export interface ValidationErrors {
  cardNumber?: ''
  cardExpiry?: ''
  cardCvc?: ''
  first_name?: ''
  last_name?: ''
}

interface StripeValidity {
  cardNumber: boolean
  cardExpiry: boolean
  cardCvc: boolean
  billingInfo: boolean
}

const initialValidity: StripeValidity = {
  cardNumber: false,
  cardExpiry: false,
  cardCvc: false,
  billingInfo: false,
}

// TODO: refactor this to use the useForm hook
// and validators pattern
const CreditCardForm: FC<Props> = ({ formData, setFormData, setToken }) => {
  const { t } = useTranslation()

  // formValidity tracks the validity of stripe iframe fields and native fields independently
  const [formValidity, setFormValidity] = useDataObject<StripeValidity>(initialValidity)
  // isValid is the source of truth for the entire form's validity
  const [isValid, setIsValid] = useState(false)
  const [stateDropdownItems, setStateDropdownItems] = useState<
    { value: string; label: string }[] | null
  >(null)

  const [validationErrors, setValidationErrors] = useDataObject<ValidationErrors>({})

  const { createTokenFromPaymentForm } = useCustomerPayment()

  // Style Stripe credit card iframe fields
  const stripeInputStyle = useMemo(() => {
    return {
      color: Storage.theme === 'dark' ? 'rgb(209, 213, 224)' : 'rgb(34,34,34)',
      fontWeight: '500',
      fontFamily: 'Graphik, sans-serif, Ariel, Helvetica, Georgia',
      fontSize: '14px',
      fontSmoothing: 'antialiased',
      ':-webkit-autofill': {
        color: 'rgb(152, 152, 152)',
      },
      '::placeholder': {
        color: 'rgb(152, 152, 152)',
      },
    }
  }, [Storage.theme])

  useEffect(() => {
    setIsValid(
      formValidity.billingInfo &&
        formValidity.cardNumber &&
        formValidity.cardExpiry &&
        formValidity.cardCvc
    )
  }, [formValidity])

  useEffect(() => {
    switch (formData.country) {
      case 'US':
        setStateDropdownItems(usStatesPlusPR)
        break
      case 'CA':
        setStateDropdownItems(canadaStates)
        break
      default:
        setStateDropdownItems(null)
        break
    }
  }, [formData.country])

  const handleValidity = (element) => {
    setValidationErrors({ [element.name]: element.validationMessage })
  }

  const handleStripeChange = (e) => {
    setFormValidity({ [e.elementType]: e.complete })
    setValidationErrors({ [e.elementType]: e.error?.message })
  }

  const handleElementChange = (target) => {
    setFormData({ [target.name]: target.value })
    setFormValidity({ billingInfo: target.form.checkValidity() })
  }

  useAsyncEffect(
    async (isActive) => {
      if (isValid) {
        const token = await createTokenFromPaymentForm(formData)
        // The validity of the PaymentForm parent component is dependent on
        // the token being set
        if (isActive() && token) setToken(token)
      } else {
        setToken(null)
      }
    },
    [formData, isValid]
  )

  return (
    <form data-testid="credit-card-form">
      <UsernameForm formData={formData} handleElementChange={handleElementChange} />

      <p className="mb-2 font-semibold">{t('content.payment')}</p>
      <label className="block w-full mb-2 text-md">{t('content.creditNumber')}</label>
      <div className="flex flex-col justify-between mb-0 bg-white rounded-sm sm:mb-2 sm:flex-row sm:p-4 sm:border border-gray-border dark:border-gray-border-dm dark:bg-black-bg2">
        <CardNumberElement
          className="w-full p-4 mb-2 border sm:mb-0 sm:w-3/4 sm:p-0 sm:border-0 border-gray-border dark:border-gray-border-dm"
          onChange={handleStripeChange}
          options={{ style: { base: stripeInputStyle } }}
        />

        <div className="flex w-full p-4 border sm:w-1/4 sm:p-0 sm:border-0 border-gray-border dark:border-gray-border-dm">
          <CardExpiryElement
            className="flex-grow"
            onChange={handleStripeChange}
            options={{ style: { base: stripeInputStyle } }}
          />

          <CardCvcElement
            className="flex-grow"
            onChange={handleStripeChange}
            options={{ style: { base: stripeInputStyle } }}
          />
        </div>
      </div>
      <div>
        {validationErrors.cardNumber && (
          <p className="text-sm text-orange">{validationErrors.cardNumber}</p>
        )}
        {validationErrors.cardExpiry && (
          <p className="text-sm text-orange">{validationErrors.cardExpiry}</p>
        )}
        {validationErrors.cardCvc && (
          <p className="text-sm text-orange">{validationErrors.cardCvc}</p>
        )}
      </div>

      <div className="flex flex-wrap mt-3 sm:flex-nowrap">
        <Input
          required
          className="mr-4 sm:basis-1/2 basis-full grow-0 shrink-0"
          label={t('content.nameOnCard')}
          minLength={1}
          name={CustomerCardAddressFields.NAME_ON_CARD}
          value={formData[CustomerCardAddressFields.NAME_ON_CARD]}
          onChange={(e) => handleElementChange(e.target)}
          onBlur={(e) => handleValidity(e.target)}
          solidBackground
        />
        <Checkbox
          className="my-4 sm:basis-1/2 basis-full grow-0 shrink-0 sm:my-0"
          name="saveCard"
          value={String(formData.saveCard)}
          onChecked={(checked) => setFormData({ saveCard: checked })}
          label="Save this card for future purchases"
        />
      </div>
      {validationErrors[CustomerCardAddressFields.NAME_ON_CARD] && (
        <p className="text-sm text-orange">
          {validationErrors[CustomerCardAddressFields.NAME_ON_CARD]}
        </p>
      )}
      <div>
        <p className="my-2 font-semibold">{t('content.billingAddress')}</p>
        <Dropdown
          label={t('content.country')}
          className="w-full sm:w-1/2"
          name={CustomerCardAddressFields.COUNTRY}
          value={formData[CustomerCardAddressFields.COUNTRY]}
          solidBackground
          onChange={(e) => handleElementChange(e.target)}>
          {countries.map(({ countryCode, labelResource }) => {
            return (
              <option key={countryCode} value={countryCode}>
                {t(labelResource)}
              </option>
            )
          })}
        </Dropdown>
        <div className="flex flex-wrap mt-3 sm:flex-nowrap">
          <Input
            label={t('content.address1')}
            className="mr-2 sm:basis-1/2 basis-full grow-0 shrink-0"
            name={CustomerCardAddressFields.ADDRESS1}
            value={formData[CustomerCardAddressFields.ADDRESS1]}
            onChange={(e) => handleElementChange(e.target)}
            onBlur={(e) => handleValidity(e.target)}
            required
            minLength={1}
            solidBackground
          />
          <Input
            label={t('content.address2')}
            className="ml-0 sm:ml-2 sm:basis-1/2 basis-full grow-0 shrink-0"
            name={CustomerCardAddressFields.ADDRESS2}
            value={formData[CustomerCardAddressFields.ADDRESS2]}
            onChange={(e) => handleElementChange(e.target)}
            solidBackground
          />
        </div>
        {validationErrors[CustomerCardAddressFields.ADDRESS1] && (
          <p className="text-sm text-orange">
            {validationErrors[CustomerCardAddressFields.ADDRESS1]}
          </p>
        )}
        <div className="flex flex-wrap items-baseline mt-3 grow-0 sm:flex-nowrap">
          <Input
            label={t('content.city')}
            required
            minLength={1}
            className="flex-grow sm:basis-1/2 basis-full"
            name={CustomerCardAddressFields.CITY}
            value={formData[CustomerCardAddressFields.CITY]}
            onChange={(e) => handleElementChange(e.target)}
            onBlur={(e) => handleValidity(e.target)}
            solidBackground
          />
          <div className="flex sm:basis-1/2 basis-full">
            {stateDropdownItems && (
              <Dropdown
                label={t('content.stateProvince')}
                className="self-end flex-grow mr-4 sm:mx-4"
                name={CustomerCardAddressFields.STATE}
                value={formData[CustomerCardAddressFields.STATE]}
                solidBackground
                onChange={(e) => handleElementChange(e.target)}>
                <option></option>
                {stateDropdownItems.map(({ value, label }) => {
                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                })}
              </Dropdown>
            )}
            {!stateDropdownItems && (
              <Input
                label={t('content.stateProvince')}
                className="self-end flex-grow mx-4"
                name={CustomerCardAddressFields.STATE}
                value={formData[CustomerCardAddressFields.STATE]}
                onChange={(e) => handleElementChange(e.target)}
                onBlur={(e) => handleValidity(e.target)}
                required
                solidBackground
                minLength={1}
              />
            )}
            <Input
              label={t('content.postalCode')}
              className="flex-grow"
              name={CustomerCardAddressFields.POSTAL_CODE}
              value={formData[CustomerCardAddressFields.POSTAL_CODE]}
              onChange={(e) => handleElementChange(e.target)}
              onBlur={(e) => handleValidity(e.target)}
              required
              pattern={formData.country == 'US' ? '[0-9]{5}' : '[a-zA-Z0-9 ]+'}
              solidBackground
            />
          </div>
        </div>
        <div>
          {validationErrors[CustomerCardAddressFields.CITY] && (
            <span className="text-sm text-orange">
              {validationErrors[CustomerCardAddressFields.CITY]}
            </span>
          )}
          {validationErrors[CustomerCardAddressFields.POSTAL_CODE] && (
            <span className="float-right text-sm text-orange">
              {validationErrors[CustomerCardAddressFields.POSTAL_CODE]}
            </span>
          )}
        </div>
      </div>
    </form>
  )
}
export default CreditCardForm
